<template>
  <!-- ═══════════════════════════════════════════════════════
       DESKTOP SIDEBAR
  ══════════════════════════════════════════════════════════ -->
  <div
    class="hidden md:flex flex-shrink-0 sticky top-0 h-screen z-40 relative"
    :style="{ width: collapsed ? '67px' : '251px', transition: 'width 0.25s cubic-bezier(0.4,0,0.2,1)' }"
  >
    <!-- Edge collapse button -->
    <button
      @click="toggleCollapse"
      class="absolute top-[18px] -right-3.5 w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 hover:scale-110 z-50"
      :style="collapseButtonStyle"
      :title="collapsed ? 'Expand' : 'Collapse'"
    >
      <svg class="w-3 h-3 transition-transform duration-200" :class="collapsed ? 'rotate-180' : ''"
        fill="none" :stroke="collapseIconColor" stroke-width="2.5" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/>
      </svg>
    </button>

    <aside class="flex flex-col h-full w-full overflow-hidden" :style="{ background: sidebarBg }">

      <!-- Org identity / switcher -->
      <div class="relative flex-shrink-0 border-b border-white/[0.12] px-3 py-2.5" ref="orgSwitcherRef">

        <!-- Multiple orgs: dropdown-styled trigger -->
        <button v-if="hasMultipleOrgs" @click="toggleOrgSwitcher" ref="orgSwitcherButtonRef"
          class="w-full flex items-center border border-white/15 transition-colors hover:bg-white/[0.08]"
          :class="[collapsed ? 'justify-center p-1.5' : 'gap-2.5 pl-1.5 pr-2 py-1.5', showOrgSwitcher ? 'rounded-t-lg border-b-transparent bg-white/[0.08]' : 'rounded-lg hover:border-white/25']">
          <div v-if="org.profile_picture_url" class="flex-shrink-0 rounded-lg overflow-hidden border border-white/20"
            style="width:28px;height:28px;min-width:28px">
            <img :src="org.profile_picture_url" :alt="org.name" class="w-full h-full object-cover">
          </div>
          <div v-else :class="org.avatar_color"
            class="flex-shrink-0 rounded-lg flex items-center justify-center text-white text-xs font-bold border border-white/20"
            style="width:28px;height:28px;min-width:28px">
            {{ org.avatar_name }}
          </div>
          <span v-show="!collapsed" class="text-[13px] font-bold text-white truncate leading-tight min-w-0 flex-1 text-left">{{ org.name }}</span>
          <svg v-show="!collapsed" class="flex-shrink-0 w-3.5 h-3.5 transition-transform duration-150 text-white/40"
            :class="showOrgSwitcher ? 'rotate-180' : ''"
            fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
          </svg>
        </button>

        <!-- Single (or not-yet-loaded) org: plain identity, nothing to switch to -->
        <a v-else :href="`/organizations/${org.url}/dashboard`" data-turbo="false"
          class="flex items-center rounded-lg py-1.5"
          :class="collapsed ? 'justify-center' : 'gap-2.5'">
          <div v-if="org.profile_picture_url" class="flex-shrink-0 rounded-lg overflow-hidden border border-white/20"
            style="width:28px;height:28px;min-width:28px">
            <img :src="org.profile_picture_url" :alt="org.name" class="w-full h-full object-cover">
          </div>
          <div v-else :class="org.avatar_color"
            class="flex-shrink-0 rounded-lg flex items-center justify-center text-white text-xs font-bold border border-white/20"
            style="width:28px;height:28px;min-width:28px">
            {{ org.avatar_name }}
          </div>
          <span v-show="!collapsed" class="text-[13px] font-bold text-white truncate leading-tight min-w-0 flex-1 text-left">{{ org.name }}</span>
        </a>

        <Teleport to="body">
          <div v-if="showOrgSwitcher && hasMultipleOrgs" class="fixed z-[9999] rounded-b-lg overflow-hidden py-1 border border-t-0 border-white/15"
            :style="orgSwitcherDropdownStyle">
            <div class="px-3.5 pt-2 pb-1.5">
              <span class="text-[10px] font-semibold uppercase tracking-widest text-white/30">Switch organization</span>
            </div>

            <div class="max-h-72 overflow-y-auto">
              <!-- Not loaded yet: skeleton rows sized from the cached org count -->
              <template v-if="!orgsLoaded">
                <div v-for="n in skeletonOrgRowCount" :key="n" class="flex items-center gap-2.5 px-3.5 py-2 animate-pulse">
                  <div class="flex-shrink-0 rounded-lg bg-white/10" style="width:26px;height:26px;min-width:26px"></div>
                  <div class="h-3 w-2/3 rounded bg-white/10"></div>
                </div>
              </template>

              <a v-else v-for="(userOrg, index) in userOrganizations" :key="userOrg.id"
                :ref="el => { if (el) orgSwitcherItemRefs[index] = el }"
                :href="orgSwitchUrl(userOrg.url)" data-turbo="false"
                class="flex items-center gap-2.5 px-3.5 py-2 transition-colors"
                :class="index === orgSwitcherActiveIndex ? 'bg-white/[0.14]' : (userOrg.id === org.id ? 'bg-white/[0.1]' : 'hover:bg-white/[0.08]')">
                <div v-if="userOrg.profile_picture_url" class="flex-shrink-0 rounded-lg overflow-hidden border border-white/20"
                  style="width:26px;height:26px;min-width:26px">
                  <img :src="userOrg.profile_picture_url" :alt="userOrg.name" class="w-full h-full object-cover">
                </div>
                <div v-else :class="userOrg.avatar_color"
                  class="flex-shrink-0 rounded-lg flex items-center justify-center text-white text-[10px] font-bold border border-white/20"
                  style="width:26px;height:26px;min-width:26px">
                  {{ userOrg.avatar_name }}
                </div>
                <span class="text-[13px] font-medium text-white/80 truncate min-w-0 flex-1">{{ userOrg.name }}</span>
                <svg v-if="userOrg.id === org.id" class="flex-shrink-0 w-3.5 h-3.5 text-white/70" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 12.75l6 6 9-13.5"/>
                </svg>
              </a>
            </div>

            <div class="border-t border-white/[0.08] mt-1"></div>
            <a href="/events" data-turbo="false"
              :ref="el => { if (el) orgSwitcherItemRefs[userOrganizations.length] = el }"
              class="flex items-center gap-2.5 px-3.5 py-2.5 transition-colors text-[13px] font-medium text-white/70"
              :class="orgSwitcherActiveIndex === userOrganizations.length ? 'bg-white/[0.14]' : 'hover:bg-white/[0.08]'">
              <svg class="flex-shrink-0 w-[15px] h-[15px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
              </svg>
              Personal Workspace
            </a>

            <div class="border-t border-white/[0.08]"></div>
            <a href="/organizations/new" data-turbo="false"
              :ref="el => { if (el) orgSwitcherItemRefs[userOrganizations.length + 1] = el }"
              class="flex items-center gap-2.5 px-3.5 py-2.5 transition-colors text-[13px] font-medium text-white/70"
              :class="orgSwitcherActiveIndex === userOrganizations.length + 1 ? 'bg-white/[0.14]' : 'hover:bg-white/[0.08]'">
              <svg class="flex-shrink-0 w-[15px] h-[15px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4"/>
              </svg>
              Create organization
            </a>
          </div>
        </Teleport>
      </div>

      <!-- Navigation -->
      <nav class="flex-1 px-2 py-3 space-y-0.5 overflow-y-auto overflow-x-hidden">

        <div v-show="!collapsed" class="px-3 pt-1 pb-2">
          <span class="text-[10px] font-semibold uppercase tracking-widest text-white/25">Workspace</span>
        </div>

        <OrgSidebarLink :to="p('dashboard')" :active="isActive('dashboard')" label="Dashboard">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
            </svg>
          </template>
          <span v-show="!collapsed">Dashboard</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('structure')" :active="route.name === 'OrganizationStructure'" label="Org Structure">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21"/>
            </svg>
          </template>
          <span v-show="!collapsed">Org Structure</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('applications')"
          :active="route.name === 'OrganizationApplications' || route.name === 'OrganizationApplicationFactsheet'" label="Applications">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6A2.25 2.25 0 016 3.75h2.25A2.25 2.25 0 0110.5 6v2.25a2.25 2.25 0 01-2.25 2.25H6a2.25 2.25 0 01-2.25-2.25V6zM3.75 15.75A2.25 2.25 0 016 13.5h2.25a2.25 2.25 0 012.25 2.25V18a2.25 2.25 0 01-2.25 2.25H6A2.25 2.25 0 013.75 18v-2.25zM13.5 6a2.25 2.25 0 012.25-2.25H18A2.25 2.25 0 0120.25 6v2.25A2.25 2.25 0 0118 10.5h-2.25a2.25 2.25 0 01-2.25-2.25V6zM13.5 15.75a2.25 2.25 0 012.25-2.25H18a2.25 2.25 0 012.25 2.25V18A2.25 2.25 0 0118 20.25h-2.25A2.25 2.25 0 0113.5 18v-2.25z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Applications</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('it_components')" :active="route.name === 'OrganizationItComponents'" label="IT Components">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5m-15 3.75H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z"/>
            </svg>
          </template>
          <span v-show="!collapsed">IT Components</span>
        </OrgSidebarLink>

        <div v-show="!collapsed" class="px-3 pt-4 pb-2">
          <span class="text-[10px] font-semibold uppercase tracking-widest text-white/25">Schedule</span>
        </div>
        <div v-show="collapsed" class="pt-2 px-2"><div class="border-t border-white/[0.12]"></div></div>

        <OrgSidebarLink :to="p('events')" :active="isActive('events')" label="Events">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
            </svg>
          </template>
          <span v-show="!collapsed">Events</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('bookings')" :active="isActive('bookings')" label="Bookings">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Bookings</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('calendar')" :active="isActive('calendar')" label="Calendar">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5M3 11.25h18"/>
            </svg>
          </template>
          <span v-show="!collapsed">Calendar</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('availabilities')" :active="isActive('availabilities')" label="Availabilities">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Availabilities</span>
        </OrgSidebarLink>

        <div v-show="!collapsed" class="px-3 pt-4 pb-2">
          <span class="text-[10px] font-semibold uppercase tracking-widest text-white/25">Insights</span>
        </div>
        <div v-show="collapsed" class="pt-2 px-2"><div class="border-t border-white/[0.12]"></div></div>

        <OrgSidebarLink :to="p('analytics')" :active="isActive('analytics')" label="Analytics">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Analytics</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('notifications')" :active="isActive('notifications')" label="Notifications">
          <template #icon>
            <div class="relative w-[18px] h-[18px]">
              <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24" class="w-full h-full">
                <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
              </svg>
              <span v-if="notifCount > 0 && collapsed"
                class="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center px-0.5">
                {{ notifCount }}
              </span>
            </div>
          </template>
          <span v-show="!collapsed" class="flex items-center gap-2 flex-1">
            Notifications
            <span v-if="notifCount > 0"
              class="ml-auto min-w-[18px] h-[18px] bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center px-1">
              {{ notifCount }}
            </span>
          </span>
        </OrgSidebarLink>

        <div class="pt-2 px-2"><div class="border-t border-white/[0.12]"></div></div>

        <OrgSidebarLink :to="p('members')" :active="isActive('members')" label="Members">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Members</span>
        </OrgSidebarLink>

        <OrgSidebarLink :to="p('activity_logs')" :active="isActive('activity_logs')" label="Activity Logs">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Activity Logs</span>
        </OrgSidebarLink>

        <OrgSidebarLink
          :to="p('profile')"
          :active="isActive('profile') || isActive('account') || isActive('plan') || isActive('developers')"
          label="Settings">
          <template #icon>
            <svg fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.325.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.241-.438.613-.43.992a7.723 7.723 0 010 .255c-.008.378.137.75.43.991l1.004.827c.424.35.534.955.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.47 6.47 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.281c-.09.543-.56.94-1.11.94h-2.594c-.55 0-1.019-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.991a6.932 6.932 0 010-.255c.007-.38-.138-.751-.43-.992l-1.004-.827a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.086.22-.128.332-.183.582-.495.644-.869l.214-1.28z"/>
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
            </svg>
          </template>
          <span v-show="!collapsed">Settings</span>
        </OrgSidebarLink>

      </nav>

      <!-- Bottom section -->
      <div class="flex-shrink-0 px-2 pt-2.5 pb-3 border-t border-white/[0.12]">

        <!-- Back to personal workspace -->
        <a href="/events" data-turbo="false"
          class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[12px] font-medium text-white/50 hover:bg-white/[0.08] hover:text-white/80 transition-colors mb-1"
          :class="collapsed ? 'justify-center' : ''"
          :title="collapsed ? 'Personal Workspace' : undefined">
          <svg class="flex-shrink-0 w-[15px] h-[15px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18"/>
          </svg>
          <span v-show="!collapsed">Personal Workspace</span>
        </a>

        <!-- User row + dropdown -->
        <div class="relative" ref="userMenuRef">

          <Teleport to="body">
            <div v-if="showUserMenu"
              class="fixed z-[9999] rounded-xl overflow-hidden"
              :style="dropdownStyle">
              <a href="/settings/profile/general" data-turbo="false"
                class="flex items-center gap-2.5 px-3.5 py-2.5 transition-colors text-[13px] font-medium text-white/70 hover:bg-white/[0.08]">
                <svg class="flex-shrink-0 w-[15px] h-[15px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                </svg>
                My Profile
              </a>
              <div class="border-t border-white/[0.08]"></div>
              <button @click="signOut"
                class="w-full flex items-center gap-2.5 px-3.5 py-2.5 transition-colors text-[13px] font-medium text-white/70 hover:bg-white/[0.08]">
                <svg class="flex-shrink-0 w-[15px] h-[15px]" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                </svg>
                Sign out
              </button>
            </div>
          </Teleport>

          <button @click="toggleUserMenu"
            ref="userMenuButtonRef"
            class="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg transition-colors hover:bg-white/[0.08]"
            :class="collapsed ? 'justify-center' : ''">
            <img v-if="user.profile_picture_url" :src="user.profile_picture_url"
              class="rounded-full object-cover flex-shrink-0"
              style="width:30px;height:30px;min-width:30px;min-height:30px;border:1.5px solid rgba(255,255,255,0.3)" />
            <div v-else
              class="rounded-full flex items-center justify-center text-white text-[10px] font-semibold flex-shrink-0"
              style="width:30px;height:30px;min-width:30px;background:rgba(37,99,235,0.6);border:1.5px solid rgba(255,255,255,0.2)">
              {{ user.avatar_name }}
            </div>
            <div v-show="!collapsed" class="min-w-0 flex-1 text-left">
              <p class="text-[12px] font-semibold truncate leading-tight text-white/80">{{ user.full_name }}</p>
              <p class="text-[10px] truncate leading-tight mt-px text-white/40">{{ user.email }}</p>
            </div>
            <svg v-show="!collapsed" class="flex-shrink-0 w-3 h-3 transition-transform duration-150 text-white/40"
              :class="showUserMenu ? 'rotate-180' : ''"
              fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>

        </div>
      </div>
    </aside>
  </div>

  <!-- ═══════════════════════════════════════════════════════
       MOBILE BOTTOM NAV
  ══════════════════════════════════════════════════════════ -->
  <nav class="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white dark:bg-[#1C2128] border-t border-gray-200 dark:border-gray-700">
    <div class="flex items-center justify-around px-2 py-1.5">

      <RouterLink :to="p('dashboard')" data-turbo-prefetch="false"
        class="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="isActive('dashboard') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
        </svg>
        <span class="text-[10px] font-medium">Dashboard</span>
      </RouterLink>

      <RouterLink :to="p('structure')" data-turbo-prefetch="false"
        class="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="route.name === 'OrganizationStructure' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21"/>
        </svg>
        <span class="text-[10px] font-medium">Org Structure</span>
      </RouterLink>

      <RouterLink :to="p('events')" data-turbo-prefetch="false"
        class="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="isActive('events') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5"/>
        </svg>
        <span class="text-[10px] font-medium">Events</span>
      </RouterLink>

      <RouterLink :to="p('bookings')" data-turbo-prefetch="false"
        class="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="isActive('bookings') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        <span class="text-[10px] font-medium">Bookings</span>
      </RouterLink>

      <RouterLink :to="p('notifications')" data-turbo-prefetch="false"
        class="relative flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="isActive('notifications') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <span class="relative">
          <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M14.857 17.082a23.848 23.848 0 005.454-1.31A8.967 8.967 0 0118 9.75V9A6 6 0 006 9v.75a8.967 8.967 0 01-2.312 6.022c1.733.64 3.56 1.085 5.455 1.31m5.714 0a24.255 24.255 0 01-5.714 0m5.714 0a3 3 0 11-5.714 0"/>
          </svg>
          <span v-if="notifCount > 0" class="absolute -top-1 -right-1 min-w-[14px] h-[14px] bg-red-500 rounded-full text-[9px] font-bold text-white flex items-center justify-center px-0.5">
            {{ notifCount }}
          </span>
        </span>
        <span class="text-[10px] font-medium">Notifications</span>
      </RouterLink>

      <RouterLink :to="p('members')" data-turbo-prefetch="false"
        class="flex flex-col items-center gap-0.5 px-3 py-1 rounded-lg transition-colors"
        :class="isActive('members') || isActive('profile') ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" stroke-width="1.75" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z"/>
        </svg>
        <span class="text-[10px] font-medium">Members</span>
      </RouterLink>

    </div>
  </nav>
</template>

<script setup>
import { ref, computed, provide, onMounted, onUnmounted, nextTick, watch } from 'vue'
import { RouterLink, useRoute } from 'vue-router'
import { orgStore } from 'controllers/vue_components/organizations/store'
import { getCachedOrganizationsCount } from 'composables/useOrganizationsCountCache'
import OrgSidebarLink from 'controllers/vue_components/SidebarLink.vue'

const store = orgStore()
const route = useRoute()

const STORAGE_KEY = 'org_sidebar_state'
const collapsed = ref(false)
provide('sidebarCollapsed', collapsed)

const showUserMenu = ref(false)
const userMenuRef = ref(null)
const userMenuButtonRef = ref(null)

const showOrgSwitcher = ref(false)
const orgSwitcherRef = ref(null)
const orgSwitcherButtonRef = ref(null)
const orgSwitcherActiveIndex = ref(-1)
const orgSwitcherItemRefs = ref([])

const isDark = ref(document.documentElement.classList.contains('dark'))
const currentTheme = ref(document.documentElement.getAttribute('data-theme') || 'light')
let darkObserver = null

const org = computed(() => store.current_org || {})
const user = computed(() => store.current_user || {})
const notifCount = computed(() => store.notifications_stats?.unviewed_notifications_count ?? 0)

const userOrganizations = computed(() => store.user_organizations || [])

// The org sidebar always has at least the current org loaded once
// fetchUserOrganizations() resolves, so an empty list means "still loading"
// here (unlike a page where zero is a real, permanent state).
const orgsLoaded = computed(() => userOrganizations.value.length > 0)

// Read synchronously at setup time (from the last successful fetch, on this
// device) so the trigger renders in its final "multiple orgs" shape on the
// very first paint instead of flashing from plain link to bordered dropdown
// once the real fetch resolves.
const cachedOrgCount = ref(getCachedOrganizationsCount())

const hasMultipleOrgs = computed(() =>
  orgsLoaded.value ? userOrganizations.value.length > 1 : cachedOrgCount.value > 1
)

// Cap so a very large cached count can't render an absurdly tall skeleton.
const skeletonOrgRowCount = computed(() => Math.min(cachedOrgCount.value, 8))

// Keyboard-navigable rows: every org, plus "Personal Workspace" and "Create organization".
const orgSwitcherItemCount = computed(() => userOrganizations.value.length + 2)

const sidebarDark = computed(() =>
  ['blue', 'oled', 'dim', 'contrast'].includes(currentTheme.value) || isDark.value
)

const sidebarBg = computed(() => {
  if (currentTheme.value === 'oled' || currentTheme.value === 'contrast') return '#000000'
  if (currentTheme.value === 'dim') return 'linear-gradient(155deg, #1c2128 0%, #22272e 45%, #1c2128 100%)'
  if (isDark.value) return 'linear-gradient(155deg, #0d1117 0%, #161b22 45%, #0d1117 100%)'
  if (currentTheme.value === 'blue') return 'linear-gradient(155deg, #0a1628 0%, #112352 45%, #0a1628 100%)'
  return 'linear-gradient(155deg, #1E40AF 0%, #2563EB 45%, #1D4ED8 100%)'
})

const collapseButtonStyle = computed(() =>
  sidebarDark.value
    ? { background: '#1C2128', boxShadow: '0 2px 10px rgba(0,0,0,0.5)', border: '1.5px solid #374151' }
    : { background: 'white', boxShadow: '0 2px 10px rgba(0,0,0,0.28)', border: '1.5px solid #d1d5db' }
)
const collapseIconColor = computed(() => sidebarDark.value ? '#93c5fd' : '#1E40AF')

const dropdownBg = computed(() => {
  if (currentTheme.value === 'oled' || currentTheme.value === 'contrast') return '#000000'
  if (currentTheme.value === 'dim') return '#1c2128'
  if (currentTheme.value === 'blue') return '#0d1e42'
  if (isDark.value) return '#161b22'
  return '#1e40af'
})

const dropdownStyle = computed(() => {
  const el = userMenuButtonRef.value
  if (!el) return {}
  const rect = el.getBoundingClientRect()
  return {
    background: dropdownBg.value,
    border: '1px solid rgba(255,255,255,0.12)',
    boxShadow: '0 -8px 28px rgba(0,0,0,0.35)',
    bottom: `${window.innerHeight - rect.top + 8}px`,
    left: `${rect.left}px`,
    width: `${Math.max(rect.width, 180)}px`,
  }
})

const orgSwitcherDropdownStyle = computed(() => {
  const el = orgSwitcherButtonRef.value
  if (!el) return {}
  const rect = el.getBoundingClientRect()
  // Flush against the trigger (no gap) and same width, so it reads as one
  // continuous element expanding downward rather than a separate floating card.
  // Collapsed sidebar is too narrow for org names, so widen it there while
  // still anchoring to the trigger's left edge.
  return {
    background: dropdownBg.value,
    boxShadow: '0 8px 20px rgba(0,0,0,0.3)',
    top: `${rect.bottom}px`,
    left: `${rect.left}px`,
    width: `${collapsed.value ? 240 : rect.width}px`,
  }
})

function toggleOrgSwitcher() {
  showOrgSwitcher.value = !showOrgSwitcher.value
  orgSwitcherActiveIndex.value = -1
  orgSwitcherItemRefs.value = []
}

function closeOrgSwitcher() {
  showOrgSwitcher.value = false
  orgSwitcherActiveIndex.value = -1
  orgSwitcherItemRefs.value = []
}

function selectOrgSwitcherItem(index) {
  const orgsCount = userOrganizations.value.length
  if (index === orgsCount) {
    window.location.href = '/events'
    return
  }
  if (index === orgsCount + 1) {
    window.location.href = '/organizations/new'
    return
  }
  const target = userOrganizations.value[index]
  if (target) window.location.href = orgSwitchUrl(target.url)
}

function handleOrgSwitcherKeydown(e) {
  if (!showOrgSwitcher.value || !hasMultipleOrgs.value || !orgsLoaded.value) return
  if (!['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return

  e.preventDefault()

  if (e.key === 'ArrowDown') {
    orgSwitcherActiveIndex.value = (orgSwitcherActiveIndex.value + 1) % orgSwitcherItemCount.value
  } else if (e.key === 'ArrowUp') {
    // -1 ("nothing selected") needs its own branch: the generic wrap formula
    // would land one short of the last row here, while ArrowDown from -1
    // correctly lands on the first row — so this keeps both directions
    // starting from "nothing selected" consistent (Down → first, Up → last).
    orgSwitcherActiveIndex.value = orgSwitcherActiveIndex.value === -1
      ? orgSwitcherItemCount.value - 1
      : (orgSwitcherActiveIndex.value - 1 + orgSwitcherItemCount.value) % orgSwitcherItemCount.value
  } else if (e.key === 'Enter' && orgSwitcherActiveIndex.value !== -1) {
    selectOrgSwitcherItem(orgSwitcherActiveIndex.value)
  }
}

watch(orgSwitcherActiveIndex, (index) => {
  if (index === -1) return
  nextTick(() => orgSwitcherItemRefs.value[index]?.scrollIntoView({ block: 'nearest' }))
})

function p(segment) {
  return `/organizations/${org.value.url}/${segment}`
}

// Top-level org page segments (config/routes.rb). Used to keep the user on
// the same section when switching organizations — anything not in this list
// (e.g. an applications/:id factsheet, which is org-specific) falls back to
// that section's list page rather than a broken/foreign URL.
const ORG_SECTION_SEGMENTS = [
  'profile', 'account', 'members', 'activity_logs', 'plan', 'appearance', 'developers',
  'dashboard', 'events', 'bookings', 'calendar', 'analytics', 'notifications',
  'availabilities', 'structure', 'applications', 'it_components',
]

function orgSwitchUrl(targetUrl) {
  const currentSection = route.path.split('/').filter(Boolean)[2]
  const section = ORG_SECTION_SEGMENTS.includes(currentSection) ? currentSection : 'dashboard'
  return `/organizations/${targetUrl}/${section}`
}

function isActive(segment) {
  return route.path.includes(`/${segment}`)
}

function toggleCollapse() {
  collapsed.value = !collapsed.value
  localStorage.setItem(STORAGE_KEY, collapsed.value ? 'collapsed' : 'expanded')
}

function toggleUserMenu() {
  showUserMenu.value = !showUserMenu.value
}

function handleClickOutside(e) {
  if (userMenuRef.value && !userMenuRef.value.contains(e.target)) showUserMenu.value = false
  if (orgSwitcherRef.value && !orgSwitcherRef.value.contains(e.target)) closeOrgSwitcher()
}

function signOut() {
  const form = document.createElement('form')
  form.method = 'post'
  form.action = '/users/sign_out'
  const m = document.createElement('input'); m.type = 'hidden'; m.name = '_method'; m.value = 'delete'
  const c = document.createElement('input'); c.type = 'hidden'; c.name = 'authenticity_token'
  c.value = document.querySelector('meta[name="csrf-token"]')?.content ?? ''
  form.appendChild(m); form.appendChild(c)
  document.body.appendChild(form); form.submit()
}

onMounted(() => {
  collapsed.value = localStorage.getItem(STORAGE_KEY) === 'collapsed'
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleOrgSwitcherKeydown)
  darkObserver = new MutationObserver(() => {
    isDark.value = document.documentElement.classList.contains('dark')
    currentTheme.value = document.documentElement.getAttribute('data-theme') || 'light'
  })
  darkObserver.observe(document.documentElement, { attributeFilter: ['class', 'data-theme'] })
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleOrgSwitcherKeydown)
  darkObserver?.disconnect()
})
</script>
